import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  History, Upload, ShieldCheck, ShieldAlert, Sparkles, RefreshCw, Pencil, Send, Play, CheckCircle, XCircle, Lock, Loader2, AlertTriangle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_META = {
  doc_uploaded:        { en: 'Document uploaded',     es: 'Documento subido',          Icon: Upload,        color: 'bg-blue-50 text-blue-700 border-blue-200' },
  submitted:           { en: 'Submitted for review',  es: 'Enviado a revisión',        Icon: Send,          color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  ai_run_triggered:    { en: 'AI run triggered',      es: 'IA ejecutada',              Icon: Play,          color: 'bg-violet-50 text-violet-700 border-violet-200' },
  ai_completed:        { en: 'AI completed',          es: 'IA completada',             Icon: Sparkles,      color: 'bg-amber-50 text-amber-700 border-amber-200' },
  manual_data_edit:    { en: 'Manual data edit',      es: 'Edición manual',            Icon: Pencil,        color: 'bg-slate-100 text-slate-700 border-slate-200' },
  name_synced:         { en: 'Name synced to Cédula', es: 'Nombre sincronizado',       Icon: RefreshCw,     color: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  approved:            { en: 'Verification approved', es: 'Verificación aprobada',     Icon: CheckCircle,   color: 'bg-green-50 text-green-700 border-green-200' },
  rejected:            { en: 'Verification rejected', es: 'Verificación rechazada',    Icon: XCircle,       color: 'bg-red-50 text-red-700 border-red-200' },
  approval_blocked:    { en: 'Approval blocked',      es: 'Aprobación bloqueada',      Icon: ShieldAlert,   color: 'bg-orange-50 text-orange-700 border-orange-200' },
};

const ACTIONS = Object.keys(ACTION_META);

function timeAgo(iso, lang) {
  if (!iso) return '';
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return lang === 'es' ? 'ahora' : 'just now';
  if (m < 60) return lang === 'es' ? `hace ${m}m` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return lang === 'es' ? `hace ${h}h` : `${h}h ago`;
  const d = Math.floor(h / 24);
  return lang === 'es' ? `hace ${d}d` : `${d}d ago`;
}

function detailSummary(entry, lang) {
  const tt = (en, es) => (lang === 'es' ? es : en);
  const d = entry.details || {};
  switch (entry.action) {
    case 'doc_uploaded':     return d.doc_type ? `${d.doc_type.replace(/_/g, ' ')}` : '';
    case 'submitted':        return d.role ? `as ${d.role}` : '';
    case 'ai_completed':     {
      const parts = [];
      if (d.face_match) parts.push(`face: ${d.face_match}`);
      if (d.confidence) parts.push(`confidence: ${d.confidence}`);
      if (typeof d.names_consistent === 'boolean') parts.push(d.names_consistent ? 'names ✓' : 'names ✗');
      return parts.join(' · ');
    }
    case 'manual_data_edit': return (d.fields_updated || []).join(', ');
    case 'name_synced':      return `"${d.previous_name || '∅'}" → "${d.new_name || ''}"`;
    case 'approved':
    case 'rejected':         return d.notes || '';
    case 'approval_blocked': return tt(`${(d.critical_issues || []).length} critical`, `${(d.critical_issues || []).length} críticas`);
    default:                 return '';
  }
}

export default function AuditLogTab() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const tt = (en, es) => (language === 'es' ? es : en);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [breaches, setBreaches] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (actionFilter && actionFilter !== 'all') params.action = actionFilter;
      params.limit = 200;
      const [logRes, breachRes] = await Promise.all([
        axios.get(`${API}/verification/admin/audit-log`, { headers, params }),
        axios.get(`${API}/verification/admin/security/rate-limits?hours=24`, { headers }).catch(() => ({ data: null })),
      ]);
      setEntries(logRes.data.entries || []);
      setBreaches(breachRes.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || tt('Failed to load audit log', 'Error al cargar registro'));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [actionFilter]);

  const filtered = entries.filter(e => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (e.target_user_name || '').toLowerCase().includes(q) ||
      (e.target_user_email || '').toLowerCase().includes(q) ||
      (e.actor_name || '').toLowerCase().includes(q) ||
      (e.actor_email || '').toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="audit-log-tab" className="space-y-4">
      <div className="bg-gradient-to-r from-[#0B192C] to-[#142944] text-white rounded-xl px-6 py-4 flex items-center gap-3">
        <History className="w-5 h-5 text-[#D4AF37]" />
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-base flex items-center gap-2">
            {tt('Verification Audit Log', 'Registro de Auditoría')}
            <Lock className="w-3.5 h-3.5 text-white/50" />
          </h3>
          <p className="text-xs text-white/70">{tt(
            'Forensic trail of every verification event — uploads, submissions, AI runs, manual edits, approvals.',
            'Registro forense de cada evento — subidas, envíos, ejecuciones IA, ediciones, aprobaciones.',
          )}</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}
          className="border-white/30 text-white hover:bg-white/10 gap-1.5"
          data-testid="audit-refresh-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {tt('Refresh', 'Refrescar')}
        </Button>
      </div>

      {/* Security · Rate-limit breaches (last 24h) */}
      {breaches && (
        <Card data-testid="security-panel" className={breaches.total > 0 ? 'border-red-300 bg-red-50/40' : 'border-green-200 bg-green-50/30'}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${breaches.total > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                {breaches.total > 0 ? <AlertTriangle className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
              </div>
              <div className="flex-1">
                <h4 className="font-heading font-semibold text-sm text-slate-900">
                  {tt('Security · Rate-Limit Breaches (last 24h)', 'Seguridad · Bloqueos de Tasa (24h)')}
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  {breaches.total === 0
                    ? tt('No abuse patterns detected. All clear.', 'Sin patrones de abuso. Todo en orden.')
                    : tt(`${breaches.total} blocks across ${breaches.by_bucket.length} endpoint(s)`, `${breaches.total} bloqueos en ${breaches.by_bucket.length} endpoint(s)`)}
                </p>
              </div>
              {breaches.total > 0 && (
                <Badge className="bg-red-600 text-white text-[11px]" data-testid="security-total-badge">
                  {breaches.total} {tt('blocks', 'bloqueos')}
                </Badge>
              )}
            </div>

            {breaches.total > 0 && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">{tt('Top endpoints', 'Endpoints')}</p>
                    <div className="space-y-1" data-testid="security-by-bucket">
                      {breaches.by_bucket.map(b => (
                        <div key={b.bucket} className="flex items-center justify-between text-xs bg-white border border-red-100 rounded-md px-2.5 py-1.5">
                          <span className="font-mono text-slate-700">{b.bucket}</span>
                          <span className="font-bold text-red-600">{b.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">{tt('Top offenders', 'Mayores ofensores')}</p>
                    <div className="space-y-1 max-h-44 overflow-y-auto" data-testid="security-by-key">
                      {breaches.by_key.map((k, i) => (
                        <div key={i} className="flex items-center justify-between text-xs bg-white border border-red-100 rounded-md px-2.5 py-1.5 gap-2">
                          <div className="min-w-0 flex-1">
                            <span className="font-mono text-slate-800 block truncate" title={k.key}>{k.key}</span>
                            <span className="text-[10px] text-slate-400">{k.bucket}</span>
                          </div>
                          <span className="font-bold text-red-600 flex-shrink-0">×{k.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 mt-3 italic">
                  {tt(
                    'Tip: clusters of breaches from many keys on the same bucket within a short window often indicate a coordinated attack.',
                    'Tip: muchos bloqueos en el mismo endpoint en poco tiempo suelen indicar un ataque coordinado.',
                  )}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          placeholder={tt('Search by user or actor name/email…', 'Buscar por nombre o email…')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="h-10 max-w-md"
          data-testid="audit-search"
        />
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="h-10 w-full sm:w-56" data-testid="audit-action-filter">
            <SelectValue placeholder={tt('Filter by action', 'Filtrar por acción')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="audit-action-all">{tt('All actions', 'Todas las acciones')}</SelectItem>
            {ACTIONS.map(a => (
              <SelectItem key={a} value={a} data-testid={`audit-action-opt-${a}`}>
                {tt(ACTION_META[a].en, ACTION_META[a].es)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-400 sm:ml-auto self-center">
          {filtered.length} {tt('entries', 'registros')}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-sm text-slate-400">
          <Loader2 className="w-5 h-5 mx-auto animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">{tt('No audit entries found', 'No hay registros')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(e => {
            const meta = ACTION_META[e.action] || { en: e.action, es: e.action, Icon: History, color: 'bg-slate-50 text-slate-600 border-slate-200' };
            const Icon = meta.Icon;
            const summary = detailSummary(e, language);
            const isSystem = e.actor_role === 'system' || !e.actor_id;
            return (
              <Card key={e.id} data-testid={`audit-row-${e.id}`} className="hover:border-slate-300 transition-colors">
                <CardContent className="p-3.5 flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${meta.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <Badge variant="outline" className={`text-[10px] ${meta.color}`}>{tt(meta.en, meta.es)}</Badge>
                        <span className="text-sm font-medium text-slate-900 truncate">{e.target_user_name || e.target_user_email}</span>
                        <Badge variant="outline" className="text-[10px] capitalize">{e.target_user_role}</Badge>
                      </div>
                      <span className="text-[11px] text-slate-400 whitespace-nowrap" title={new Date(e.created_at).toLocaleString()}>
                        {timeAgo(e.created_at, language)}
                      </span>
                    </div>
                    {summary && (
                      <p className="text-xs text-slate-500 mt-1 break-words">{summary}</p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1">
                      {tt('by', 'por')}{' '}
                      <span className={`font-medium ${isSystem ? 'text-slate-500 italic' : 'text-slate-600'}`}>
                        {isSystem ? tt('System (AI)', 'Sistema (IA)') : (e.actor_name || e.actor_email)}
                      </span>
                      {!isSystem && e.actor_role && (
                        <span className="text-slate-300"> · {e.actor_role}</span>
                      )}
                      <span className="text-slate-300"> · </span>
                      <span className="font-mono">{new Date(e.created_at).toLocaleString()}</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
